import { http } from './request';
import type {
  Customer,
  CreateCustomerRequest,
  Proposal,
  CreateProposalRequest,
  RequirementAnalysis,
  AnalyzeRequirementRequest,
  ProductInsight,
  Product,
  PricingPlan,
  GeneratePricingRequest,
  UpdatePricingRequest,
  ReferenceInfluencerList,
  UpdateReferenceRequest,
  ProposalDocument,
  GenerateDocumentRequest,
} from '../types/proposal';
import type { PaginationParams } from '../types/api';

export const proposalApi = {
  // 客户管理
  getCustomers: (params?: PaginationParams) =>
    http.getPaginated<Customer>('/api/v1/customers', params),

  createCustomer: (data: CreateCustomerRequest) =>
    http.post<Customer>('/api/v1/customers', data),

  getCustomer: (customerId: string) =>
    http.get<Customer>(`/api/v1/customers/${customerId}`),

  updateCustomer: (customerId: string, data: Partial<CreateCustomerRequest>) =>
    http.put<Customer>(`/api/v1/customers/${customerId}`, data),

  deleteCustomer: (customerId: string) =>
    http.delete<void>(`/api/v1/customers/${customerId}`),

  // 客户产品管理
  getProducts: (customerId: string) =>
    http.get<Product[]>(`/api/v1/customers/${customerId}/products`),

  createProduct: (customerId: string, data: {
    name: string;
    category?: string;
    description?: string;
    product_url?: string;
    benchmark_brands?: Array<{ name: string; link?: string }>;
  }) =>
    http.post<Product>(`/api/v1/customers/${customerId}/products`, data),

  updateProduct: (customerId: string, productId: string, data: {
    name?: string;
    category?: string;
    description?: string;
    product_url?: string;
    benchmark_brands?: Array<{ name: string; link?: string }>;
  }) =>
    http.put<Product>(`/api/v1/customers/${customerId}/products/${productId}`, data),

  deleteProduct: (customerId: string, productId: string) =>
    http.delete<void>(`/api/v1/customers/${customerId}/products/${productId}`),

  // 提案管理
  getProposals: (params?: PaginationParams) =>
    http.getPaginated<Proposal>('/api/v1/proposals', params),

  createProposal: (data: CreateProposalRequest) =>
    http.post<Proposal>('/api/v1/proposals', data),

  getProposal: (proposalId: string) =>
    http.get<Proposal>(`/api/v1/proposals/${proposalId}`),

  updateProposal: (proposalId: string, data: Partial<CreateProposalRequest> & { status?: string }) =>
    http.put<Proposal>(`/api/v1/proposals/${proposalId}`, data),

  deleteProposal: (proposalId: string) =>
    http.delete<void>(`/api/v1/proposals/${proposalId}`),

  // 需求解构
  analyzeRequirements: (proposalId: string, data: AnalyzeRequirementRequest) =>
    http.post<RequirementAnalysis>(`/api/v1/proposals/${proposalId}/analyze-requirements`, data),

  getRequirementAnalysis: (proposalId: string) =>
    http.get<RequirementAnalysis>(`/api/v1/proposals/${proposalId}/requirements`),

  updateRequirement: (proposalId: string, requirementId: string, data: { confirmation_status?: string; user_modified?: Record<string, unknown> }) =>
    http.put<RequirementAnalysis>(`/api/v1/proposals/${proposalId}/requirements/${requirementId}`, data),

  // 产品洞察
  generateProductInsight: (proposalId: string) =>
    http.post<ProductInsight>(`/api/v1/proposals/${proposalId}/product-insight`),

  getProductInsight: (proposalId: string) =>
    http.get<ProductInsight>(`/api/v1/proposals/${proposalId}/product-insight`),

  // 报价方案
  generatePricing: (proposalId: string, data?: GeneratePricingRequest) =>
    http.post<PricingPlan>(`/api/v1/proposals/${proposalId}/pricing`, data),

  getPricing: (proposalId: string) =>
    http.get<PricingPlan>(`/api/v1/proposals/${proposalId}/pricing`),

  updatePricing: (pricingId: string, data: UpdatePricingRequest) =>
    http.put<PricingPlan>(`/api/v1/pricing/${pricingId}`, data),

  // 参考红人
  generateReferences: (proposalId: string, maxResults?: number) => {
    const url = maxResults
      ? `/api/v1/proposals/${proposalId}/references?max_results=${maxResults}`
      : `/api/v1/proposals/${proposalId}/references`;
    return http.post<ReferenceInfluencerList>(url);
  },

  getReferences: (proposalId: string) =>
    http.get<ReferenceInfluencerList>(`/api/v1/proposals/${proposalId}/references`),

  updateReference: (referenceId: string, data: UpdateReferenceRequest) =>
    http.put<{ message: string }>(`/api/v1/references/${referenceId}`, data),

  deleteReference: (referenceId: string) =>
    http.delete<{ message: string }>(`/api/v1/references/${referenceId}`),

  // 文档生成
  generateDocument: (proposalId: string, data: GenerateDocumentRequest) =>
    http.post<ProposalDocument>(`/api/v1/proposals/${proposalId}/document`, data),

  getDocument: (proposalId: string) =>
    http.get<ProposalDocument>(`/api/v1/proposals/${proposalId}/document`),

  downloadDocument: (documentId: string) => {
    return http.get<Blob>(`/api/v1/documents/${documentId}/download`, undefined, { responseType: 'blob' });
  },
};
